import clsx from 'clsx';
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, MouseEvent } from 'react';
import { trackCTAClick } from '@/lib/analytics';

type ButtonVariant = 'primary' | 'secondary' | 'ghost';
type ButtonSize = 'sm' | 'base' | 'lg';

type AnchorProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  as?: 'a';
  variant?: ButtonVariant;
  size?: ButtonSize;
  trackCta?: {
    text: string;
    section: string;
    variant?: string;
    id?: string;
  };
};

type NativeButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  as?: 'button';
  variant?: ButtonVariant;
  size?: ButtonSize;
  trackCta?: {
    text: string;
    section: string;
    variant?: string;
    id?: string;
  };
};

type ButtonProps = AnchorProps | NativeButtonProps;

const baseClass =
  'inline-flex items-center justify-center rounded-full font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 disabled:cursor-not-allowed disabled:opacity-60';

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-white text-black hover:bg-gray-50 active:bg-gray-100',
  secondary:
    'bg-transparent border-2 border-white/30 text-white hover:bg-white/10',
  ghost:
    'bg-transparent text-white/90 hover:text-white hover:bg-white/5',
};

const sizeClasses: Record<ButtonVariant, Record<ButtonSize, string>> = {
  primary: {
    sm: 'px-6 py-2.5 text-sm',
    base: 'px-8 py-3 text-base',
    lg: 'px-10 py-4 text-lg',
  },
  secondary: {
    sm: 'px-4 py-2 text-sm',
    base: 'px-6 py-2 text-base',
    lg: 'px-8 py-3 text-lg',
  },
  ghost: {
    sm: 'px-3 py-1.5 text-sm',
    base: 'px-4 py-2 text-base',
    lg: 'px-5 py-2.5 text-lg',
  },
};

function resolveElement(props: ButtonProps, asOverride?: ButtonProps['as']) {
  if (asOverride) {
    return asOverride;
  }

  if ('href' in props && props.href !== undefined) {
    return 'a';
  }

  return 'button';
}

function Button(props: ButtonProps) {
  const {
    variant = 'primary',
    size = 'base',
    className,
    as,
    trackCta,
    onClick,
    ...rest
  } = props as ButtonProps & { className?: string };

  const element = resolveElement(props, as);

  const classes = clsx(
    baseClass,
    variantClasses[variant],
    sizeClasses[variant][size],
    className,
  );

  if (element === 'a') {
    return (
      <a
        {...(rest as AnchorProps)}
        className={classes}
        onClick={(event) => {
          if (trackCta) {
            trackCTAClick(
              trackCta.text,
              trackCta.section,
              trackCta.variant ?? variant,
              trackCta.id
            );
          }
          onClick?.(event as unknown as MouseEvent<HTMLAnchorElement>);
        }}
      />
    );
  }

  const { type, ...buttonProps } = rest as NativeButtonProps;

  return (
    <button
      {...buttonProps}
      type={type ?? 'button'}
      className={classes}
      onClick={(event) => {
        if (trackCta) {
          trackCTAClick(
            trackCta.text,
            trackCta.section,
            trackCta.variant ?? variant,
            trackCta.id
          );
        }
        onClick?.(event as unknown as MouseEvent<HTMLButtonElement>);
      }}
    />
  );
}

export default Button;
export { Button };
